import {CbError} from '@/hosted_fields/common/errors';
import Logger from '@/utils/logger_old';
import {isValidURL} from '@/utils/utility-functions';

let merchantDomainBlocked = false;

/**
 * Checks if hostOrigin is allowed based on allowedDomains (supports wildcards).
 */
export function isDomainAllowed(hostOrigin: string, allowedDomains: string[]): boolean {
  const url = new URL(hostOrigin);
  const normalizedOrigin = `${url.protocol}//${url.host}`.toLowerCase();
  const normalizedHost = url.hostname.toLowerCase();

  for (const domain of allowedDomains) {
    const normalizedDomain = domain.toLowerCase();

    if (normalizedOrigin === normalizedDomain) {
      return true;
    }

    if (normalizedDomain.startsWith('https://*') || normalizedDomain.startsWith('http://*')) {
      const baseDomain = normalizedDomain.replace(/^https?:\/\//, '').replace(/^\*\./, '');
      const expectedProtocol = normalizedDomain.startsWith('https://') ? 'https:' : 'http:';
      if (url.protocol !== expectedProtocol) {
        continue;
      }
      if (normalizedHost === baseDomain || normalizedHost.endsWith(`.${baseDomain}`)) {
        return true;
      }
      continue;
    }

    let domainUrl: URL | null = null;
    try {
      domainUrl = new URL(normalizedDomain.startsWith('http') ? normalizedDomain : `https://${normalizedDomain}`);
    } catch (err) {
      continue;
    }

    if (domainUrl && url.protocol !== domainUrl.protocol) {
      continue;
    }

    if (domainUrl && normalizedOrigin === domainUrl.origin) {
      return true;
    }
  }
  return false;
}

export type MerchantDomainValidationResult = {
  blocked: boolean;
  hostOrigin?: string;
};

export type MerchantDomainValidationInfo = {
  cb_js_domain_validation_enabled?: boolean;
  allowed_domains?: Array<string>;
};

/**
 * When cb_js_domain_validation_enabled is true in retrieve_js_info, the merchant page origin must
 * match allowed_domains.
 */
export function evaluateMerchantDomainValidation(
  info: MerchantDomainValidationInfo,
  windowUrl?: string
): MerchantDomainValidationResult {
  if (!info.cb_js_domain_validation_enabled) {
    return {blocked: false};
  }

  const allowedDomains = info.allowed_domains || [];
  if (!allowedDomains.length || !windowUrl || !isValidURL(windowUrl)) {
    return {blocked: true};
  }

  const hostOrigin = new URL(windowUrl).origin;
  return {
    blocked: !isDomainAllowed(hostOrigin, allowedDomains),
    hostOrigin,
  };
}

export function isMerchantDomainBlocked(): boolean {
  return merchantDomainBlocked;
}

/** Called once after retrieve_js_info; gates subsequent master API calls. */
export function updateMerchantDomainValidation(info: MerchantDomainValidationInfo, windowUrl?: string): void {
  const wasBlocked = merchantDomainBlocked;
  const {blocked, hostOrigin} = evaluateMerchantDomainValidation(info, windowUrl);
  merchantDomainBlocked = blocked;
  if (blocked && !wasBlocked) {
    Logger.kvl({
      action: 'merchant_domain_validation_failed',
      host_origin: hostOrigin,
      window_url: windowUrl,
      allowed_domains: info.allowed_domains,
    });
  }
}

export function merchantDomainNotAllowedError(): CbError {
  return new CbError({
    name: 'domain_not_allowed',
    message:
      'This domain is not allowed to use Chargebee.js. Add it to allowed domains in your Chargebee site settings.',
  });
}
